import { supabase } from './client'
import { supabaseAdmin } from './admin'

const SITE_URL = process.env.REACT_APP_SITE_URL || 'https://bgiassets.com'

// ============================================
// AUTHENTICATION
// ============================================

export async function registerUser({ email, password, firstName, lastName, phone, referralCode, referred_by }) {
  try {
    console.log('🚀 Starting registration for:', email)

    // ── STEP 1: Create auth user WITH metadata ───────────────
    const { data: authData, error: authError } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: {
          full_name:  `${firstName} ${lastName}`.trim(),
          first_name: firstName,
          last_name:  lastName,
          phone:      phone,
          role:       'client'
        },
        emailRedirectTo: `${SITE_URL}/login`
      }
    })

    if (authError) {
      console.error('❌ Auth signup error:', authError)
      throw authError
    }

    if (!authData.user) throw new Error('No user data returned from signup')
    console.log('✅ Auth user created:', authData.user.id)

    // Small delay to let DB trigger fire first
    await new Promise(resolve => setTimeout(resolve, 1000))

    // ── STEP 2: Try RPC (handles wallet creation too) ────────
    try {
      const { error: rpcError } = await supabase.rpc('create_user_profile', {
        p_user_id:    authData.user.id,
        p_email:      email,
        p_first_name: firstName,
        p_last_name:  lastName,
        p_phone:      phone
      })
      if (rpcError) console.warn('⚠️ RPC failed, using direct upsert:', rpcError.message)
      else console.log('✅ Profile created via RPC')
    } catch (e) {
      console.warn('⚠️ RPC threw exception:', e.message)
    }

    // ── STEP 3: Guaranteed profile upsert ────────────────────
    // Always runs — updates NULL fields even if trigger/RPC ran
    const { error: profileError } = await supabase
      .from('profiles')
      .upsert(
        {
          id:         authData.user.id,
          email:      email,
          first_name: firstName,     // ✅ guaranteed non-NULL
          last_name:  lastName,      // ✅ guaranteed non-NULL
          phone:      phone,         // ✅ guaranteed non-NULL
          role:       'client',
          status:     'active',
          kyc_status: 'pending',
          updated_at: new Date().toISOString()
        },
        { onConflict: 'id' }
      )

    if (profileError) {
      console.error('❌ Profile upsert error:', profileError)
      return { success: true, user: authData.user, warning: `Profile save failed: ${profileError.message}` }
    }

    console.log('✅ Profile saved → first_name:', firstName, '| last_name:', lastName, '| phone:', phone)

    // ── STEP 4: Ensure wallet exists ─────────────────────────
    // RPC should create it, but guarantee it exists regardless
    await ensureWalletExists(authData.user.id)

    // ── STEP 5: Link referral ────────────────────────────────
    if (referred_by) {
      const { error: refError } = await supabase
        .from('profiles')
        .update({ referred_by })
        .eq('id', authData.user.id)

      if (refError) console.warn('⚠️ Failed to update referred_by:', refError.message)
      else console.log('✅ Referral link saved')
    }

    console.log('🎉 Registration completed!')
    return { success: true, user: authData.user }

  } catch (error) {
    console.error('💥 Registration failed:', error)
    return { success: false, error: error.message || 'Registration failed. Please try again.' }
  }
}

export async function loginUser({ email, password }) {
  try {
    const { data, error } = await supabase.auth.signInWithPassword({ email, password })
    if (error) throw error

    const { data: profile } = await supabase
      .from('profiles')
      .select('*')
      .eq('id', data.user.id)
      .single()

    // Ensure wallet exists on login too (safety net for old accounts)
    await ensureWalletExists(data.user.id)

    return { success: true, user: data.user, profile, session: data.session }
  } catch (error) {
    console.error('Login error:', error)
    return { success: false, error: error.message }
  }
}

export async function logoutUser() {
  const { error } = await supabase.auth.signOut()
  if (error) console.error('Logout error:', error)
  return !error
}

export async function getCurrentUser() {
  try {
    const { data: { session } } = await supabase.auth.getSession()
    if (!session) return null

    const { data: profile } = await supabase
      .from('profiles')
      .select('*')
      .eq('id', session.user.id)
      .single()

    return { user: session.user, profile }
  } catch (error) {
    console.error('Get current user error:', error)
    return null
  }
}

export async function resetPassword(email) {
  try {
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/reset-password`
    })
    return error
      ? { success: false, error: error.message }
      : { success: true, message: 'Password reset email sent' }
  } catch (error) {
    return { success: false, error: 'An error occurred' }
  }
}

export async function updatePassword(newPassword) {
  try {
    const { error } = await supabase.auth.updateUser({ password: newPassword })
    return error ? { success: false, error: error.message } : { success: true }
  } catch (error) {
    return { success: false, error: 'An error occurred' }
  }
}

// ============================================
// PROFILE MANAGEMENT
// ============================================

export async function getUserProfile(userId) {
  const { data, error } = await supabase
    .from('profiles')
    .select('*')
    .eq('id', userId)
    .single()

  if (error) throw error
  return data
}

export async function updateUserProfile(userId, updates) {
  const { data, error } = await supabase
    .from('profiles')
    .update(updates)
    .eq('id', userId)
    .select()
    .single()

  if (error) throw error
  return data
}

// ============================================
// KYC MANAGEMENT
// ============================================

export async function uploadKYCDocument({ userId, documentType, file, documentNumber }) {
  try {
    const fileExt = file.name.split('.').pop()
    const fileName = `${userId}/${documentType}-${Date.now()}.${fileExt}`

    const { error: uploadError } = await supabase.storage
      .from('kyc-documents')
      .upload(fileName, file)

    if (uploadError) throw uploadError

    const { data: { publicUrl } } = supabase.storage
      .from('kyc-documents')
      .getPublicUrl(fileName)

    const { data, error } = await supabase
      .from('kyc_documents')
      .insert([{ user_id: userId, document_type: documentType, document_number: documentNumber, document_url: publicUrl, status: 'pending' }])
      .select()
      .single()

    if (error) throw error
    return { success: true, document: data }
  } catch (error) {
    console.error('KYC upload error:', error)
    return { success: false, error: error.message }
  }
}

export async function getUserKYCDocuments(userId) {
  const { data, error } = await supabase
    .from('kyc_documents')
    .select('*')
    .eq('user_id', userId)
    .order('created_at', { ascending: false })

  if (error) throw error
  return data
}

export async function updateKYCStatus(documentId, status, rejectionReason = null, verifiedBy) {
  const { data, error } = await supabase
    .from('kyc_documents')
    .update({ status, rejection_reason: rejectionReason, verified_by: verifiedBy, verified_at: new Date().toISOString() })
    .eq('id', documentId)
    .select()
    .single()

  if (error) throw error
  return data
}

// ============================================
// WALLET MANAGEMENT
// ============================================

/**
 * ✅ FIXED: Uses .maybeSingle() instead of .single()
 * .single() throws PGRST116 (406) when 0 rows found.
 * .maybeSingle() returns null safely.
 */
export async function getUserWallet(userId) {
  try {
    const { data, error } = await supabase
      .from('wallets')
      .select('*')
      .eq('user_id', userId)
      .maybeSingle()                         // ✅ FIXED: was .single()

    if (error) {
      console.error('Get wallet error:', error)
      return null
    }

    // If no wallet exists yet, create one
    if (!data) {
      console.log('⚠️ No wallet found for user, creating one:', userId)
      return await ensureWalletExists(userId)
    }

    return data
  } catch (error) {
    console.error('Get wallet error:', error)
    return null
  }
}

/**
 * Creates a wallet for the user if one doesn't already exist.
 * Safe to call multiple times — uses upsert.
 */
export async function ensureWalletExists(userId) {
  try {
    // Check first
    const { data: existing } = await supabase
      .from('wallets')
      .select('*')
      .eq('user_id', userId)
      .maybeSingle()

    if (existing) return existing

    // Create wallet
    const { data: newWallet, error: createError } = await supabase
      .from('wallets')
      .insert([{
        user_id:           userId,
        available_balance: 0,
        locked_balance:    0,
        total_balance:     0,
        currency:          'USD',
        last_transaction_at: null
      }])
      .select()
      .single()

    if (createError) {
      // Might be a race condition — try to fetch again
      if (createError.code === '23505') {
        const { data: raceWallet } = await supabase
          .from('wallets')
          .select('*')
          .eq('user_id', userId)
          .maybeSingle()
        return raceWallet
      }
      console.error('❌ Wallet creation error:', createError)
      return null
    }

    console.log('✅ Wallet created for user:', userId)
    return newWallet
  } catch (error) {
    console.error('ensureWalletExists error:', error)
    return null
  }
}

export async function updateWalletBalance(userId, amount, type = 'add') {
  try {
    console.log(`💳 [START] Updating wallet balance for user ${userId}: ${type} ${amount}`)

    const wallet = await getUserWallet(userId)          // uses fixed version above
    if (!wallet) throw new Error('Wallet not found')

    const currentAvailable = parseFloat(wallet.available_balance || 0)
    const amountNum        = parseFloat(amount)
    const newAvailable     = type === 'add' ? currentAvailable + amountNum : currentAvailable - amountNum

    const { data: updatedWallet, error: updateError } = await supabase
      .from('wallets')
      .update({ available_balance: newAvailable, last_transaction_at: new Date().toISOString() })
      .eq('user_id', userId)
      .select()
      .single()

    if (updateError) throw updateError

    console.log('✅ [SUCCESS] Wallet updated:', updatedWallet)
    return updatedWallet
  } catch (error) {
    console.error('💥 [FATAL] Update wallet balance error:', error)
    throw error
  }
}

// ============================================
// PAYMENT SETTINGS (BROKER)
// ============================================

export async function getPaymentSettings() {
  try {
    const { data, error } = await supabase
      .from('broker_payment_settings')
      .select('*')
      .maybeSingle()                                    // ✅ FIXED: was .single()

    if (error) {
      console.error('Error fetching payment settings:', error)
      return { upi: { enabled: false }, bank: { enabled: false }, usdt: { enabled: false }, hawala: { enabled: false } }
    }

    return data?.settings || { upi: { enabled: false }, bank: { enabled: false }, usdt: { enabled: false }, hawala: { enabled: false } }
  } catch (error) {
    console.error('Get payment settings error:', error)
    return { upi: { enabled: false }, bank: { enabled: false }, usdt: { enabled: false }, hawala: { enabled: false } }
  }
}

export async function updatePaymentSettings(settings) {
  try {
    const { data, error } = await supabase
      .from('broker_payment_settings')
      .upsert({ id: 1, settings, updated_at: new Date().toISOString() })
      .select()
      .single()

    if (error) throw error
    return { success: true, data }
  } catch (error) {
    console.error('Update payment settings error:', error)
    return { success: false, error: error.message }
  }
}

// ============================================
// DEPOSITS
// ============================================

export async function createDeposit({ userId, amount, paymentMethod, mt5Login, utrNumber, proofFile, hawalaData }) {
  try {
    let proofUrl = null

    if (proofFile) {
      const fileExt = proofFile.name.split('.').pop()
      const fileName = `${userId}/deposit-${Date.now()}.${fileExt}`
      const { error: uploadError } = await supabase.storage.from('payment-proofs').upload(fileName, proofFile)
      if (uploadError) throw uploadError
      const { data: { publicUrl } } = supabase.storage.from('payment-proofs').getPublicUrl(fileName)
      proofUrl = publicUrl
    }

    const depositData = { user_id: userId, mt5_login: mt5Login, amount, payment_method: paymentMethod, utr_number: utrNumber, payment_proof_url: proofUrl, status: 'pending' }

    if (paymentMethod === 'hawala' && hawalaData) {
      depositData.hawala_from_city     = hawalaData.fromCity
      depositData.hawala_to_city       = hawalaData.toCity
      depositData.hawala_company       = hawalaData.company
      depositData.hawala_secret_number = hawalaData.secretNumber
    }

    const { data, error } = await supabase.from('deposits').insert([depositData]).select().single()
    if (error) throw error
    return { success: true, deposit: data }
  } catch (error) {
    console.error('Deposit creation error:', error)
    return { success: false, error: error.message }
  }
}

export async function getUserDeposits(userId) {
  try {
    const { data, error } = await supabase.from('deposits').select('*').eq('user_id', userId).order('created_at', { ascending: false })
    if (error) throw error
    return data || []
  } catch (error) {
    console.error('Get deposits error:', error)
    return []
  }
}

export async function updateDepositStatus(depositId, status, processedBy, rejectionReason = null) {
  try {
    console.log('💰 [STEP 1] Starting deposit status update:', { depositId, status, processedBy })

    const { data: deposit, error: depositError } = await supabase
      .from('deposits')
      .update({ status, processed_by: processedBy, processed_at: new Date().toISOString(), rejection_reason: rejectionReason })
      .eq('id', depositId)
      .select()
      .single()

    if (depositError) throw depositError
    console.log('✅ [STEP 1 COMPLETE] Deposit updated:', deposit)

    if (status === 'approved') {
      const wallet = await getUserWallet(deposit.user_id)    // uses safe version
      if (!wallet) throw new Error('Wallet not found for user ' + deposit.user_id)

      const newBalance = parseFloat(wallet.available_balance || 0) + parseFloat(deposit.amount)

      const { data: updatedWallet, error: walletUpdateError } = await supabase
        .from('wallets')
        .update({ available_balance: newBalance, last_transaction_at: new Date().toISOString() })
        .eq('user_id', deposit.user_id)
        .select()
        .single()

      if (walletUpdateError) throw walletUpdateError
      console.log('✅ [STEP 2 COMPLETE] Wallet updated:', updatedWallet)

      try {
        const refreshedWallet = await getUserWallet(deposit.user_id)
        const balanceBefore = parseFloat(refreshedWallet.total_balance || 0) - parseFloat(deposit.amount)
        const balanceAfter  = parseFloat(refreshedWallet.total_balance || 0)

        const { error: transactionError } = await supabase
          .from('transactions')
          .insert([{ user_id: deposit.user_id, wallet_id: refreshedWallet.id, transaction_type: 'deposit', amount: parseFloat(deposit.amount), balance_before: balanceBefore, balance_after: balanceAfter, reference_id: depositId, description: `Deposit approved - ${deposit.payment_method}`, metadata: {} }])

        if (transactionError) console.error('❌ Transaction creation failed (non-fatal):', transactionError)
        else console.log('✅ [STEP 3 COMPLETE] Transaction created')
      } catch (e) {
        console.error('❌ Transaction creation failed (non-fatal):', e)
      }
    }

    console.log('🎉 Deposit approval completed!')
    return deposit
  } catch (error) {
    console.error('💥 Update deposit status failed:', error)
    throw error
  }
}

// ============================================
// WITHDRAWALS
// ============================================

export async function createWithdrawal({ userId, amount, mt5Login, mt5Balance, withdrawalMethod, accountHolderName, accountNumber, ifscCode, bankName, upiId }) {
  try {
    const wallet = await getUserWallet(userId)
    if (!wallet || parseFloat(wallet.available_balance) < parseFloat(amount)) {
      return { success: false, error: 'Insufficient balance' }
    }

    const { data, error } = await supabase
      .from('withdrawals')
      .insert([{ user_id: userId, mt5_login: mt5Login, amount, mt5_balance: mt5Balance || 0, withdrawal_method: withdrawalMethod, account_holder_name: accountHolderName, account_number: accountNumber, ifsc_code: ifscCode, bank_name: bankName, upi_id: upiId, status: 'pending' }])
      .select()
      .single()

    if (error) throw error

    await supabase
      .from('wallets')
      .update({ available_balance: parseFloat(wallet.available_balance) - parseFloat(amount), locked_balance: parseFloat(wallet.locked_balance || 0) + parseFloat(amount) })
      .eq('user_id', userId)

    return { success: true, withdrawal: data }
  } catch (error) {
    console.error('Withdrawal creation error:', error)
    return { success: false, error: error.message }
  }
}

export async function getUserWithdrawals(userId) {
  try {
    const { data, error } = await supabase.from('withdrawals').select('*').eq('user_id', userId).order('created_at', { ascending: false })
    if (error) throw error
    return data || []
  } catch (error) {
    console.error('Get withdrawals error:', error)
    return []
  }
}

export async function updateWithdrawalStatus(withdrawalId, status, processedBy, rejectionReason = null) {
  const { data: withdrawal, error } = await supabase
    .from('withdrawals')
    .update({ status, processed_by: processedBy, processed_at: new Date().toISOString(), rejection_reason: rejectionReason })
    .eq('id', withdrawalId)
    .select()
    .single()

  if (error) throw error

  const wallet = await getUserWallet(withdrawal.user_id)

  if (status === 'approved') {
    await supabase.from('wallets')
      .update({ locked_balance: parseFloat(wallet.locked_balance || 0) - parseFloat(withdrawal.amount), total_balance: parseFloat(wallet.total_balance) - parseFloat(withdrawal.amount) })
      .eq('user_id', withdrawal.user_id)

    await createTransaction({ userId: withdrawal.user_id, transactionType: 'withdrawal', amount: withdrawal.amount, referenceId: withdrawalId, description: `Withdrawal processed - ${withdrawal.withdrawal_method}` })
  } else if (status === 'rejected') {
    await supabase.from('wallets')
      .update({ available_balance: parseFloat(wallet.available_balance) + parseFloat(withdrawal.amount), locked_balance: parseFloat(wallet.locked_balance || 0) - parseFloat(withdrawal.amount) })
      .eq('user_id', withdrawal.user_id)
  }

  return withdrawal
}

// ============================================
// TRANSACTIONS
// ============================================

export async function createTransaction({ userId, transactionType, amount, referenceId, description, metadata = {} }) {
  try {
    const wallet = await getUserWallet(userId)
    if (!wallet) throw new Error('Wallet not found for user')

    const balanceBefore = parseFloat(wallet.total_balance || 0)
    const amountNum     = parseFloat(amount)
    const balanceAfter  = transactionType === 'deposit' ? balanceBefore + amountNum : balanceBefore - amountNum

    const { data: transaction, error: transactionError } = await supabase
      .from('transactions')
      .insert([{ user_id: userId, wallet_id: wallet.id, transaction_type: transactionType, amount: amountNum, balance_before: balanceBefore, balance_after: balanceAfter, reference_id: referenceId, description: description || `${transactionType} transaction`, metadata: metadata || {} }])
      .select()
      .single()

    if (transactionError) throw transactionError
    return transaction
  } catch (error) {
    console.error('💥 Create transaction failed:', error)
    throw error
  }
}

export async function getUserTransactions(userId, limit = 50) {
  try {
    const { data, error } = await supabase.from('transactions').select('*').eq('user_id', userId).order('created_at', { ascending: false }).limit(limit)
    if (error) throw error
    return data || []
  } catch (error) {
    console.error('Get transactions error:', error)
    return []
  }
}

// ============================================
// MT5 ACCOUNTS
// ============================================

export async function createMT5Account(userId, accountGroup = 'LIVE PRO', leverage = 500) {
  try {
    const loginId  = Math.floor(100000000 + Math.random() * 900000000).toString()
    const password = Math.random().toString(36).slice(-8)

    const { data, error } = await supabase
      .from('mt5_accounts')
      .insert([{ user_id: userId, login_id: loginId, password, account_group: accountGroup, leverage, balance: 0, equity: 0, margin: 0, free_margin: 0, margin_level: 0, currency: 'USD' }])
      .select()
      .single()

    if (error) throw error
    return data
  } catch (error) {
    console.error('Create MT5 account error:', error)
    return null
  }
}

export async function getUserMT5Accounts(userId) {
  try {
    const { data, error } = await supabase.from('mt5_accounts').select('*').eq('user_id', userId)
    if (error) throw error
    return data || []
  } catch (error) {
    console.error('Get MT5 accounts error:', error)
    return []
  }
}

// ============================================
// SUPPORT TICKETS
// ============================================

export async function createSupportTicket({ userId, subject, category, priority = 'medium' }) {
  try {
    const { data, error } = await supabase.from('support_tickets').insert([{ user_id: userId, subject, category, priority, status: 'open' }]).select().single()
    if (error) throw error
    return data
  } catch (error) {
    console.error('Create ticket error:', error)
    return null
  }
}

export async function addTicketMessage({ ticketId, userId, message, attachments = [] }) {
  try {
    const { data, error } = await supabase.from('ticket_messages').insert([{ ticket_id: ticketId, user_id: userId, message, attachments }]).select().single()
    if (error) throw error
    return data
  } catch (error) {
    console.error('Add ticket message error:', error)
    return null
  }
}

export async function getUserTickets(userId) {
  try {
    const { data: tickets, error } = await supabase.from('support_tickets').select('*').eq('user_id', userId).order('created_at', { ascending: false })
    if (error) throw error
    if (!tickets) return []

    return await Promise.all(
      tickets.map(async (ticket) => {
        const { data: messages } = await supabase.from('ticket_messages').select('*').eq('ticket_id', ticket.id).order('created_at', { ascending: true })
        return { ...ticket, ticket_messages: messages || [] }
      })
    )
  } catch (error) {
    return []
  }
}

// ============================================
// ADMIN FUNCTIONS
// ============================================

export async function getAllClients(filters = {}) {
  try {
    let query = supabase.from('profiles').select('*, wallets(*), mt5_accounts(*)').eq('role', 'client')
    if (filters.status)    query = query.eq('status',     filters.status)
    if (filters.kycStatus) query = query.eq('kyc_status', filters.kycStatus)
    const { data, error } = await query.order('created_at', { ascending: false })
    if (error) throw error
    return data || []
  } catch (error) {
    console.error('Get all clients error:', error)
    return []
  }
}

export async function getDashboardStats() {
  try {
    const { count: totalClients }   = await supabase.from('profiles').select('*', { count: 'exact', head: true }).eq('role', 'client')
    const { count: totalIBs }       = await supabase.from('profiles').select('*', { count: 'exact', head: true }).in('role', ['master', 'super_master'])
    const today = new Date().toISOString().split('T')[0]
    const { data: todayDeposits }   = await supabase.from('deposits').select('amount').eq('status', 'approved').gte('created_at', today)
    const todayDepositAmount        = todayDeposits?.reduce((sum, d) => sum + parseFloat(d.amount), 0) || 0
    const { count: pendingKYC }     = await supabase.from('kyc_documents').select('*', { count: 'exact', head: true }).eq('status', 'pending')
    const { count: activeAccounts } = await supabase.from('mt5_accounts').select('*', { count: 'exact', head: true }).eq('is_active', true)
    return { totalClients: totalClients || 0, totalIBs: totalIBs || 0, todayDepositAmount, pendingKYC: pendingKYC || 0, activeAccounts: activeAccounts || 0 }
  } catch (error) {
    console.error('Get dashboard stats error:', error)
    return { totalClients: 0, totalIBs: 0, todayDepositAmount: 0, pendingKYC: 0, activeAccounts: 0 }
  }
}

// ============================================
// BROKER ADMIN FUNCTIONS
// ============================================

export async function getAllClientsForBroker() {
  try {
    const { data, error } = await supabase.from('profiles').select('*').eq('role', 'client').order('created_at', { ascending: false })
    if (error) throw error

    if (data?.length > 0) {
      return await Promise.all(
        data.map(async (client) => {
          const { data: wallets }     = await supabase.from('wallets').select('*').eq('user_id', client.id)
          const { data: mt5Accounts } = await supabase.from('mt5_accounts').select('*').eq('user_id', client.id)
          return { ...client, wallets: wallets || [], mt5_accounts: mt5Accounts || [] }
        })
      )
    }
    return data || []
  } catch (error) {
    console.error('Get all clients error:', error)
    return []
  }
}

export async function getAllDeposits() {
  try {
    const { data, error } = await supabase.from('deposits').select('*').order('created_at', { ascending: false })
    if (error) throw error
    if (data?.length > 0) {
      return await Promise.all(
        data.map(async (deposit) => {
          const { data: profile } = await supabase.from('profiles').select('first_name, last_name, email').eq('id', deposit.user_id).maybeSingle()
          return { ...deposit, profiles: profile || { first_name: 'Unknown', last_name: 'User', email: 'N/A' } }
        })
      )
    }
    return data || []
  } catch (error) {
    console.error('Get all deposits error:', error)
    return []
  }
}

export async function getAllWithdrawals() {
  try {
    const { data, error } = await supabase.from('withdrawals').select('*').order('created_at', { ascending: false })
    if (error) throw error
    if (data?.length > 0) {
      return await Promise.all(
        data.map(async (w) => {
          const { data: profile } = await supabase.from('profiles').select('first_name, last_name, email').eq('id', w.user_id).maybeSingle()
          return { ...w, profiles: profile || { first_name: 'Unknown', last_name: 'User', email: 'N/A' } }
        })
      )
    }
    return data || []
  } catch (error) {
    console.error('Get all withdrawals error:', error)
    return []
  }
}

export async function getAllKYCDocuments() {
  try {
    const { data, error } = await supabase.from('kyc_documents').select('*').order('created_at', { ascending: false })
    if (error) throw error
    if (data?.length > 0) {
      return await Promise.all(
        data.map(async (doc) => {
          const { data: profile } = await supabase.from('profiles').select('first_name, last_name, email, phone').eq('id', doc.user_id).maybeSingle()
          return { ...doc, profiles: profile || { first_name: 'Unknown', last_name: 'User', email: 'N/A', phone: 'N/A' } }
        })
      )
    }
    return data || []
  } catch (error) {
    console.error('Get all KYC documents error:', error)
    return []
  }
}

export async function getAllIBs() {
  try {
    const { data, error } = await supabase.from('profiles').select('*').in('role', ['master', 'super_master']).order('created_at', { ascending: false })
    if (error) throw error
    if (data?.length > 0) {
      return await Promise.all(
        data.map(async (ib) => {
          const { data: wallets } = await supabase.from('wallets').select('*').eq('user_id', ib.id)
          return { ...ib, wallets: wallets || [] }
        })
      )
    }
    return data || []
  } catch (error) {
    console.error('Get all IBs error:', error)
    return []
  }
}

export async function getAllPayouts() {
  try {
    const { data, error } = await supabase.from('payouts').select('*').order('created_at', { ascending: false })
    if (error) throw error
    if (data?.length > 0) {
      return await Promise.all(
        data.map(async (payout) => {
          const { data: profile } = await supabase.from('profiles').select('first_name, last_name, email').eq('id', payout.beneficiary_id).maybeSingle()
          return { ...payout, profiles: profile || { first_name: 'Unknown', last_name: 'User', email: 'N/A' } }
        })
      )
    }
    return data || []
  } catch (error) {
    console.error('Get all payouts error:', error)
    return []
  }
}

export async function getAllRebates() {
  try {
    const { data, error } = await supabase.from('rebates').select('*').order('created_at', { ascending: false })
    if (error) throw error
    if (data?.length > 0) {
      return await Promise.all(
        data.map(async (rebate) => {
          const { data: profile } = await supabase.from('profiles').select('first_name, last_name, email').eq('id', rebate.user_id).maybeSingle()
          return { ...rebate, profiles: profile || { first_name: 'Unknown', last_name: 'User', email: 'N/A' } }
        })
      )
    }
    return data || []
  } catch (error) {
    console.error('Get all rebates error:', error)
    return []
  }
}

export async function getAllSupportTickets() {
  try {
    const { data, error } = await supabase.from('support_tickets').select('*').order('created_at', { ascending: false })
    if (error) throw error
    if (data?.length > 0) {
      return await Promise.all(
        data.map(async (ticket) => {
          const { data: profile }  = await supabase.from('profiles').select('first_name, last_name, email').eq('id', ticket.user_id).maybeSingle()
          const { data: messages } = await supabase.from('ticket_messages').select('*').eq('ticket_id', ticket.id).order('created_at', { ascending: true })
          return { ...ticket, profiles: profile || { first_name: 'Unknown', last_name: 'User', email: 'N/A' }, ticket_messages: messages || [] }
        })
      )
    }
    return data || []
  } catch (error) {
    console.error('Get all support tickets error:', error)
    return []
  }
}

export async function getAllWallets() {
  try {
    const { data, error } = await supabase.from('wallets').select('*').order('updated_at', { ascending: false })
    if (error) throw error
    if (data?.length > 0) {
      return await Promise.all(
        data.map(async (wallet) => {
          const { data: profile } = await supabase.from('profiles').select('first_name, last_name, email').eq('id', wallet.user_id).maybeSingle()
          return { ...wallet, profiles: profile || { first_name: 'Unknown', last_name: 'User', email: 'N/A' } }
        })
      )
    }
    return data || []
  } catch (error) {
    console.error('Get all wallets error:', error)
    return []
  }
}

export async function updatePayoutStatus(payoutId, status, processedBy, utrNumber = null) {
  try {
    const { data, error } = await supabase.from('payouts').update({ status, processed_by: processedBy, processed_at: new Date().toISOString(), utr_number: utrNumber }).eq('id', payoutId).select().single()
    if (error) throw error
    return data
  } catch (error) {
    console.error('Update payout status error:', error)
    return null
  }
}

export async function updateRebateStatus(rebateId, status) {
  try {
    const { data, error } = await supabase.from('rebates').update({ status, approved_at: status === 'approved' ? new Date().toISOString() : null, paid_at: status === 'approved' ? new Date().toISOString() : null }).eq('id', rebateId).select().single()
    if (error) throw error
    return data
  } catch (error) {
    console.error('Update rebate status error:', error)
    return null
  }
}

export async function updateTicketStatus(ticketId, status) {
  try {
    const { data, error } = await supabase.from('support_tickets').update({ status, updated_at: new Date().toISOString() }).eq('id', ticketId).select().single()
    if (error) throw error
    return data
  } catch (error) {
    console.error('Update ticket status error:', error)
    return null
  }
}

export async function updateClientStatus(userId, status) {
  try {
    const { data, error } = await supabase.from('profiles').update({ status }).eq('id', userId).select().single()
    if (error) throw error
    return data
  } catch (error) {
    console.error('Update client status error:', error)
    return null
  }
}

// ============================================
// MANAGER FUNCTIONS
// ============================================

export async function getAllManagers(brokerId) {
  try {
    const { data: { session } } = await supabase.auth.getSession()
    if (!session) return null
    const { data, error } = await supabase.from('managers').select('*').eq('broker_id', session.user.id).order('created_at', { ascending: false })
    if (error) throw error
    return data || []
  } catch (error) {
    console.error('Get managers error:', error)
    return []
  }
}

export async function getManagerById(managerId) {
  try {
    const { data, error } = await supabase.from('managers').select('*').eq('id', managerId).maybeSingle()
    if (error) throw error
    return data
  } catch (error) {
    console.error('Get manager error:', error)
    return null
  }
}

export async function getManagerByProfileId(managerId) {
  try {
    const { data, error } = await supabase.from('managers').select('*').eq('user_id', managerId).maybeSingle()
    if (error) throw error
    return data
  } catch (error) {
    console.error('Get manager error:', error)
    return null
  }
}

export async function createManager({ brokerId, name, email, mt5_server, mt5_manager_id, mt5_password, password }) {
  try {
    const { data: authData, error: authError } = await supabase.auth.signUp({
      email, password,
      options: { data: { full_name: name, first_name: name, last_name: '', phone: '', role: 'manager' } }
    })
    if (authError) throw authError
    if (!authData.user) throw new Error('No user data returned from signup')

    await new Promise(resolve => setTimeout(resolve, 1000))

    try {
      await supabase.rpc('create_user_profile', { p_user_id: authData.user.id, p_email: email, p_first_name: name, p_last_name: '', p_phone: '' })
    } catch (_) { /* fallback below */ }

    await supabase.from('profiles').upsert(
      { id: authData.user.id, email, first_name: name, last_name: '', phone: '', role: 'manager', status: 'active', kyc_status: 'pending', updated_at: new Date().toISOString() },
      { onConflict: 'id' }
    )

    await ensureWalletExists(authData.user.id)

    const { data: managerData, error: managerError } = await supabase
      .from('managers')
      .insert([{ broker_id: brokerId, user_id: authData.user.id, name, email, mt5_server, mt5_manager_id, mt5_password, manager_status: 'active', mt5_last_connected_at: null }])
      .select()
      .single()

    if (managerError) throw managerError
    return { success: true, manager: managerData }
  } catch (error) {
    console.error('Create manager error:', error)
    return { success: false, error: error.message }
  }
}

export async function updateManagerMT5Credentials(managerId, { mt5_server, mt5_manager_id, mt5_password }) {
  try {
    const { data, error } = await supabase.from('managers').update({ mt5_server, mt5_manager_id, mt5_password, updated_at: new Date().toISOString() }).eq('id', managerId).select().single()
    if (error) throw error
    return { success: true, manager: data }
  } catch (error) {
    console.error('Update manager credentials error:', error)
    return { success: false, error: error.message }
  }
}

export async function updateManagerStatus(managerId, status) {
  try {
    const { data, error } = await supabase.from('managers').update({ manager_status: status, mt5_last_connected_at: status === 'active' ? new Date().toISOString() : null, updated_at: new Date().toISOString() }).eq('id', managerId).select().single()
    if (error) throw error
    return { success: true, manager: data }
  } catch (error) {
    console.error('Update manager status error:', error)
    return { success: false, error: error.message }
  }
}

export async function deleteManager(managerId) {
  try {
    const { error } = await supabase.from('managers').delete().eq('id', managerId)
    if (error) throw error
    return { success: true }
  } catch (error) {
    console.error('Delete manager error:', error)
    return { success: false, error: error.message }
  }
}

export async function testManagerConnection(managerId) {
  try {
    const manager = await getManagerById(managerId)
    if (!manager) return { success: false, error: 'Manager not found' }
    await new Promise(resolve => setTimeout(resolve, 1500))
    const { data, error } = await supabase.from('managers').update({ mt5_last_connected_at: new Date().toISOString() }).eq('id', managerId).select().single()
    if (error) throw error
    return { success: true, message: 'Connection successful', manager: data }
  } catch (error) {
    console.error('Test manager connection error:', error)
    return { success: false, error: error.message }
  }
}